import { ChevronLeftIcon, Icon } from "@chakra-ui/icons";
import { Divider, useColorModeValue } from "@chakra-ui/react";
import {
  EventDetailComponentType,
  EventDetailComponentTypes,
  EventType,
  NFTEvent,
  NFTEventInfo,
} from "@models/event";
import { DEFAULT_LANGUAGE } from "@models/language";
import { User } from "@models/user";
import { isEmpty } from "lodash";
import Link from "next/link";
import { Fragment, useMemo } from "react";
import EventDetailAbout from "./EventDetailAbout";
import EventDetailAboutMoongate from "./EventDetailAboutMoongate";
import EventDetailBanner, { ActionButtonStyles } from "./EventDetailBanner";
import EventDetailEligibility from "./EventDetailEligibility";
import EventDetailFAQ from "./EventDetailFAQ";
import EventDetailFAQLink from "./EventDetailFAQLink";
import EventDetailHostInfo from "./EventDetailHostInfo";
import {
  EventDetailGeneralInfo,
  EventDetailInfo,
  EventDetailTicketInfo,
} from "./EventDetailInfo";
import EventDetailTag from "./EventDetailTag";
import EventDetailTicketType from "./EventDetailTicketType";

const components = {
  [EventDetailComponentTypes.generalInfo]: EventDetailGeneralInfo,
  [EventDetailComponentTypes.ticketInfo]: EventDetailTicketInfo,
  [EventDetailComponentTypes.info]: EventDetailInfo,
  [EventDetailComponentTypes.ticketType]: EventDetailTicketType,
  [EventDetailComponentTypes.eligibility]: EventDetailEligibility,
  [EventDetailComponentTypes.about]: EventDetailAbout,
  [EventDetailComponentTypes.hostInfo]: EventDetailHostInfo,
  [EventDetailComponentTypes.aboutMoongate]: EventDetailAboutMoongate,
  [EventDetailComponentTypes.faqLink]: EventDetailFAQLink,
  [EventDetailComponentTypes.faq]: EventDetailFAQ,
};

const EventDetailComponentDividerWrapper = ({
  children,
  showDivider,
}: {
  children: React.ReactNode;
  showDivider: boolean;
}) => (
  <>
    {children}
    {showDivider && <Divider w="2.5rem" borderColor="neutral.200" />}
  </>
);

const EventDetail = ({
  event,
  user,
  componentsOrder: componentsOrder = [],
  showContactUs = true,
  actionButtonStyles,
  dateTimeLocationContainerClassName,
  dateTimeLocationIconClassName,
  dateTimeLocationIconContainerClassName,
  infoNameClassName,
  showDivider = false,
  showBackButton = true,
}: {
  event: NFTEvent;
  user: User | null;
  componentsOrder: EventDetailComponentType[];
  showContactUs?: boolean;
  actionButtonStyles?: ActionButtonStyles;
  dateTimeLocationContainerClassName?: string;
  dateTimeLocationIconClassName?: string;
  dateTimeLocationIconContainerClassName?: string;
  infoNameClassName?: string;
  showDivider?: boolean;
  showBackButton?: boolean;
}) => {
  const language = DEFAULT_LANGUAGE;
  const info: NFTEventInfo = useMemo(() => {
    if (!event) return new NFTEventInfo({ name: "" });
    return event.localeInfo(language);
  }, [event, language]);

  const showAppNavBar = user && event.type === EventType.BENEFIT;
  const backIconColor = useColorModeValue("", "neutral.300");

  const order = useMemo(
    () => event.customizations?.eventDetailComponentsOrder || componentsOrder,
    [event.customizations?.eventDetailComponentsOrder]
  );

  /* console.log("order", { order }); */

  const _showDivider = useMemo(
    () => event.customizations?.eventDetailShowDivider || showDivider,
    [event.customizations?.eventDetailShowDivider]
  );

  return (
    <div className="container">
      <div
        className={`w-full h-full min-h-full flex flex-col md:flex-row md:justify-center px-4 lg:px-0 items-center md:items-start ${
          showAppNavBar ? "" : "pt-8"
        }`}
      >
        {/* Main Content */}
        <div className="w-full flex flex-col items-start flex-grow md:mr-10">
          <div className="text-[2rem] mb-8 dark:text-white">
            {showAppNavBar && showBackButton && (
              <Link href="/benefits">
                <div className="flex flex-row items-center mb-4 text-neutral-300">
                  <Icon
                    as={ChevronLeftIcon}
                    boxSize={"1.25rem"}
                    color={backIconColor}
                    cursor="pointer"
                  />
                  <p className="text-[1rem] cursor-pointer">Back</p>
                </div>
              </Link>
            )}
            {/* Back Button (only for Benefit and logged in) */}
            <p className={infoNameClassName}>{info.name}</p>
            {info.subtitle && <p className="py-4 text-2xl">{info.subtitle}</p>}
            {<EventDetailTag tags={event.tags} eventType={event.type} />}
          </div>

          <div className="flex md:hidden w-full">
            <EventDetailBanner
              event={event}
              info={info}
              showContactUs={showContactUs}
              actionButtonStyles={actionButtonStyles}
            />
          </div>

          <div className="flex flex-col gap-16 mb-36">
            {order.map((component, index) => {
              const Component = components[component] as any;
              if (!Component) return <Fragment key={index} />;
              const d = _showDivider && index < order.length - 1;

              return (
                <Fragment key={index}>
                  {component === EventDetailComponentTypes.generalInfo && (
                    <EventDetailComponentDividerWrapper showDivider={d}>
                      <Component event={event} info={info} />
                    </EventDetailComponentDividerWrapper>
                  )}

                  {component === EventDetailComponentTypes.ticketInfo &&
                    event.type === EventType.TICKET && (
                      <EventDetailComponentDividerWrapper showDivider={d}>
                        <Component
                          event={event}
                          info={info}
                          dateTimeLocationContainerClassName={
                            dateTimeLocationContainerClassName
                          }
                          dateTimeLocationIconClassName={
                            dateTimeLocationIconClassName
                          }
                          dateTimeLocationIconContainerClassName={
                            dateTimeLocationIconContainerClassName
                          }
                        />
                      </EventDetailComponentDividerWrapper>
                    )}

                  {component === EventDetailComponentTypes.info &&
                    (event.type === EventType.TICKET ||
                      (info.organizer && info.organizer.name) ||
                      (info.externalLinks &&
                        info.externalLinks.length > 0)) && (
                      <EventDetailComponentDividerWrapper showDivider={d}>
                        <Component
                          event={event}
                          info={info}
                          dateTimeLocationContainerClassName={
                            dateTimeLocationContainerClassName
                          }
                          dateTimeLocationIconClassName={
                            dateTimeLocationIconClassName
                          }
                          dateTimeLocationIconContainerClassName={
                            dateTimeLocationIconContainerClassName
                          }
                        />
                      </EventDetailComponentDividerWrapper>
                    )}

                  {component === EventDetailComponentTypes.ticketType && (
                    <EventDetailComponentDividerWrapper showDivider={d}>
                      <Component event={event} />
                    </EventDetailComponentDividerWrapper>
                  )}

                  {component === EventDetailComponentTypes.eligibility &&
                    event.type === EventType.BENEFIT && (
                      <EventDetailComponentDividerWrapper showDivider={d}>
                        <Component event={event} user={user} />
                      </EventDetailComponentDividerWrapper>
                    )}

                  {component === EventDetailComponentTypes.about &&
                    !isEmpty(info.descriptions) &&
                    info.descriptions.map((description, index) => (
                      <EventDetailComponentDividerWrapper
                        key={index}
                        showDivider={d}
                      >
                        <Component
                          key={index}
                          title={description.title}
                          content={description.content}
                        />
                      </EventDetailComponentDividerWrapper>
                    ))}

                  {component === EventDetailComponentTypes.hostInfo &&
                    !isEmpty(info.organizer) &&
                    info.organizer.name &&
                    info.organizer.description && (
                      <EventDetailComponentDividerWrapper showDivider={d}>
                        <Component info={info} />
                      </EventDetailComponentDividerWrapper>
                    )}

                  {component === EventDetailComponentTypes.aboutMoongate && (
                    <EventDetailComponentDividerWrapper showDivider={d}>
                      <Component event={event} />
                    </EventDetailComponentDividerWrapper>
                  )}

                  {component === EventDetailComponentTypes.faqLink && (
                    <EventDetailComponentDividerWrapper showDivider={d}>
                      <Component />
                    </EventDetailComponentDividerWrapper>
                  )}

                  {component === EventDetailComponentTypes.faq &&
                    !isEmpty(info.faqs) && (
                      <EventDetailComponentDividerWrapper showDivider={d}>
                        <Component faqs={info.faqs} />
                      </EventDetailComponentDividerWrapper>
                    )}
                </Fragment>
              );
            })}
          </div>
        </div>

        {/* Side Banner */}
        <div className="col-span-4 flex-shrink-0 min-h-0 hidden md:block sticky top-4 self-start w-[20rem] lg:w-[24rem] xl:w-[28rem] ">
          <EventDetailBanner
            event={event}
            info={info}
            showContactUs={showContactUs}
            actionButtonStyles={actionButtonStyles}
          />
        </div>
      </div>
    </div>
  );
};

export default EventDetail;
